import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from 'react-router-dom';
import SummaryApi from "../common/index";
import GridView from "../component/admincomponent/GridView";
import ListView from "../component/admincomponent/ListView";
import {
  FaPencilAlt,
  FaSearch,
  FaTh,
  FaBars,
  FaTimes,
  FaEye,FaPlus
} from "react-icons/fa";
import { encodeCourseId } from "../utils/encoder";

const CourseList = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedCategory, setSelectedCategory] = useState("");
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState({
    title: "",
    category: "",
    duration: "",
    lessons: "",
    rating: "",
    status: "",
  });
  const [showFilters, setShowFilters] = useState({
    title: false,
    category: false,
    duration: false,
    lessons: false,
    rating: false,
    status: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const itemsPerPage = 8;

  // Theme detection - follow admin-theme from localStorage and listen for changes
  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    // Initial theme setup
    updateTheme();

    // Listen for theme changes from AdminPanel
    const handleThemeChange = () => {
      updateTheme();
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'admin-theme') {
        updateTheme();
      }
    };

    // Listen for custom admin theme events
    const handleAdminThemeToggled = () => {
      updateTheme();
    };

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  const formatImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http")) return imagePath;
    const baseUrl = SummaryApi.Images.url.replace("/api", "");
    if (imagePath.includes(baseUrl)) return imagePath;
    if (imagePath.startsWith("uploads/")) return `${baseUrl}/${imagePath}`;
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (courses.length > 0) {
      let filtered = courses;
      if (selectedCategory) {
        filtered = filtered.filter((course) =>
          course.category_name?.toLowerCase() === selectedCategory.toLowerCase()
        );
      }
      setFilteredCourses(filtered);
      setCurrentPage(1);
    }
  }, [courses, selectedCategory]);

  const uniqueCategories = useMemo(() => {
    const categories = new Set();
    courses.forEach(course => {
      if (course.category_name) categories.add(course.category_name);
    });
    return Array.from(categories).sort();
  }, [courses]);

  const fetchCourses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(SummaryApi.CourseList.url, {
        method: SummaryApi.CourseList.method,
      });
      const data = await response.json();
      if (data.status) {
        setCourses(data.data || []);
      } else {
        toast.error(data.message || "Failed to fetch courses");
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Error fetching courses");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (course) => {
    const newTab = window.open(
      `/admin-panel/create-course?id=${course.course_id}`,
      "_blank"
    );
    const checkTabClosed = setInterval(() => {
      if (newTab.closed) {
        clearInterval(checkTabClosed);
        fetchCourses();
      }
    }, 1000);
  };

  const handleView = (course) => {
    const encodedId = encodeCourseId(course.course_id);
    navigate(`/admin-panel/course-details?id=${encodedId}`);
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setColumnFilters({
      title: "", category: "", duration: "", lessons: "", rating: "", status: ""
    });
    setShowFilters({
      title: false, category: false, duration: false, lessons: false, rating: false, status: false
    });
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSelectedCategory("");
    setCurrentPage(1);
  };

  const renderRating = (rating) => {
    let effectiveRating = parseFloat(rating);
    if (isNaN(effectiveRating) || effectiveRating < 0) effectiveRating = 0;
    if (effectiveRating > 5) effectiveRating = 5;
    const fullStars = Math.floor(effectiveRating);
    const hasHalfStar = effectiveRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <svg key={`full-${i}`} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        {hasHalfStar && (
          <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <svg key={`empty-${i}`} className="w-4 h-4 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">
          ({effectiveRating.toFixed(1)})
        </span>
      </div>
    );
  };

  const renderStatus = (isActive) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      isActive === 1
        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
    }`}>
      {isActive === 1 ? "Active" : "Inactive"}
    </span>
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentCourses = filteredCourses.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCourses.length / itemsPerPage);

  const downloadCSV = () => {
    const headers = ["Title", "Category", "Duration", "Lessons", "Difficulty", "Language", "Rating", "Status"];
    const rows = filteredCourses.map((course) => [
      course.course_title || "N/A",
      course.category_name || "N/A",
      course.course_duration || "N/A",
      course.total_lessions || "N/A",
      course.diffculty_level || "N/A",
      course.language_instruction || "N/A",
      course.rating || "N/A",
      course.is_active === 1 ? "Active" : "Inactive",
    ]);
    let csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" +
      rows.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "courses.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFilter = (key) => {
    setShowFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);
  const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  const matchesColumnFilters = (course) => {
    return (
      (course.course_title?.toLowerCase().includes(columnFilters.title.toLowerCase()) || !columnFilters.title) &&
      (course.category_name?.toLowerCase().includes(columnFilters.category.toLowerCase()) || !columnFilters.category) &&
      (columnFilters.duration ? course.course_duration && parseFloat(course.course_duration) >= parseFloat(columnFilters.duration) : true) &&
      (columnFilters.lessons ? course.total_lessions && parseInt(course.total_lessions) >= parseInt(columnFilters.lessons) : true) &&
      (columnFilters.rating ? course.rating && parseFloat(course.rating) >= parseFloat(columnFilters.rating) : true) &&
      (columnFilters.status ? (course.is_active === 1 ? "active" : "inactive").includes(columnFilters.status.toLowerCase()) : true)
    );
  };

const renderGridItem = (course, index) => (
  <div
    className={`p-3 sm:p-4 dark:bg-gray-700 flex flex-col h-full rounded-lg shadow-sm transition-all duration-300`}
  >
    {/* Title + Status */}
    <div className="flex justify-between items-center mb-2">
      <h3
        className={`text-xs sm:text-sm md:text-base font-semibold line-clamp-2 flex-grow transition-colors duration-300 ${
          isDarkMode ? "text-white" : "text-gray-800"
        }`}
        title={course.course_title || "Untitled Course"}
      >
        {course.course_title || "Untitled Course"}
      </h3>
      <div className="ml-2">{renderStatus(course.is_active)}</div>
    </div>

    {/* Thumbnail */}
    <div className="mb-3">
      {course.course_thumbnail ? (
        <img
          src={formatImageUrl(course.course_thumbnail)}
          alt={course.course_title}
          className="h-32 sm:h-40 md:h-44 lg:h-48 w-full object-cover rounded-md"
        />
      ) : (
        <div className="h-32 sm:h-40 md:h-44 lg:h-48 w-full rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
          <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">
            No Image
          </span>
        </div>
      )}
    </div>

    {/* Tags */}
    <div className="mb-3">
      <div className="text-[10px] sm:text-xs flex flex-wrap gap-2 justify-start">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full font-bold text-[10px] ${
            course.category_name
              ? isDarkMode
                ? "bg-indigo-900 text-indigo-200"
                : "bg-indigo-100 text-indigo-800"
              : isDarkMode
                ? "bg-gray-700 text-gray-300"
                : "bg-gray-100 text-gray-800"
          }`}
        >
          {course.category_name || "Uncategorized"}
        </span>

        <span
          className={`inline-flex items-center px-2 py-1 rounded-full font-medium text-[10px] ${
            course.course_duration
              ? isDarkMode
                ? "bg-blue-900 text-blue-200"
                : "bg-blue-100 text-blue-800"
              : isDarkMode
                ? "bg-gray-700 text-gray-300"
                : "bg-gray-100 text-gray-800"
          }`}
        >
          {course.course_duration || "N/A"}
        </span>
      </div>
    </div>

    {/* Footer */}
    <div className="mt-auto">
      <div className="flex justify-between items-center">
        <div>{renderRating(course.rating)}</div>

        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(course)}
            className={`p-1 rounded transition-colors duration-200 ${
              isDarkMode
                ? "text-blue-500 hover:text-blue-700 hover:bg-white"
                : "text-[#1f4774] hover:text-blue-600"
            }`}
            title="Edit"
          >
            <FaPencilAlt className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
);


  const renderListHeader = () => (
    <>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>Image</th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>
        <div className="flex flex-col items-center gap-1">
          Title
          <div className="flex items-center gap-1">
            <button 
              onClick={() => toggleFilter("title")} 
              className={`transition-colors duration-200 ${
                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <FaSearch size={12} />
            </button>
            {showFilters.title && (
              <input
                type="text"
                placeholder="Filter Title"
                value={columnFilters.title}
                onChange={(e) => setColumnFilters({ ...columnFilters, title: e.target.value })}
                className={`w-24 px-2 py-1 border rounded-md text-xs transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
            )}
          </div>
        </div>
      </th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>
        <div className="flex flex-col items-center gap-1">
          Category
          <div className="flex items-center gap-1">
            <button 
              onClick={() => toggleFilter("category")} 
              className={`transition-colors duration-200 ${
                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <FaSearch size={12} />
            </button>
            {showFilters.category && (
              <input
                type="text"
                placeholder="Filter Category"
                value={columnFilters.category}
                onChange={(e) => setColumnFilters({ ...columnFilters, category: e.target.value })}
                className={`w-24 px-2 py-1 border rounded-md text-xs transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
            )}
          </div>
        </div>
      </th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>
        <div className="flex flex-col items-center gap-1">
          Duration
          <div className="flex items-center gap-1">
            <button 
              onClick={() => toggleFilter("duration")} 
              className={`transition-colors duration-200 ${
                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <FaSearch size={12} />
            </button>
            {showFilters.duration && (
              <input
                type="number"
                placeholder="Min Duration"
                value={columnFilters.duration}
                onChange={(e) => setColumnFilters({ ...columnFilters, duration: e.target.value })}
                className={`w-24 px-2 py-1 border rounded-md text-xs transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
            )}
          </div>
        </div>
      </th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>
        <div className="flex flex-col items-center gap-1">
          Lessons
          <div className="flex items-center gap-1">
            <button 
              onClick={() => toggleFilter("lessons")} 
              className={`transition-colors duration-200 ${
                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <FaSearch size={12} />
            </button>
            {showFilters.lessons && (
              <input
                type="number"
                placeholder="Min Lessons"
                value={columnFilters.lessons}
                onChange={(e) => setColumnFilters({ ...columnFilters, lessons: e.target.value })}
                className={`w-24 px-2 py-1 border rounded-md text-xs transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
            )}
          </div>
        </div>
      </th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>
        <div className="flex flex-col items-center gap-1">
          Rating
          <div className="flex items-center gap-1">
            <button 
              onClick={() => toggleFilter("rating")} 
              className={`transition-colors duration-200 ${
                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <FaSearch size={12} />
            </button>
            {showFilters.rating && (
              <input
                type="number"
                placeholder="Min Rating"
                min="0"
                max="5"
                step="0.1"
                value={columnFilters.rating}
                onChange={(e) => setColumnFilters({ ...columnFilters, rating: e.target.value })}
                className={`w-24 px-2 py-1 border rounded-md text-xs transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              />
            )}
          </div>
        </div>
      </th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>
        <div className="flex flex-col items-center gap-1">
          Status
          <div className="flex items-center gap-1">
            <button 
              onClick={() => toggleFilter("status")} 
              className={`transition-colors duration-200 ${
                isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <FaSearch size={12} />
            </button>
            {showFilters.status && (
              <select
                value={columnFilters.status}
                onChange={(e) => setColumnFilters({ ...columnFilters, status: e.target.value })}
                className={`w-24 px-2 py-1 border rounded-md text-xs transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            )}
          </div>
        </div>
      </th>
      <th className={`border px-3 py-2 font-semibold text-center align-middle transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-50 text-gray-800 border-gray-300'
      }`}>Actions</th>
    </>
  );

  const renderListRow = (course, index) => {
    const matches = matchesColumnFilters(course);
    return (
      <>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
        }`}>
          {matches && course.course_thumbnail ? (
            <img 
              src={formatImageUrl(course.course_thumbnail)} 
              alt={course.course_title} 
              className="h-12 w-12 object-contain rounded mx-auto" 
            />
          ) : (
            <div className="h-12 w-12 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center mx-auto">
              <span className="text-gray-500 dark:text-gray-400 text-xs">No Image</span>
            </div>
          )}
        </td>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'
        }`}>
          {matches ? course.course_title || "Untitled Course" : <span className="text-gray-400">N/A</span>}
        </td>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'
        }`}>
          {matches ? course.category_name || "Uncategorized" : <span className="text-gray-400">N/A</span>}
        </td>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'
        }`}>
          {matches ? course.course_duration || "N/A" : <span className="text-gray-400">N/A</span>}
        </td>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'
        }`}>
          {matches ? course.total_lessions || "N/A" : <span className="text-gray-400">N/A</span>}
        </td>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
        }`}>
          {matches ? renderRating(course.rating) : <span className="text-gray-400">N/A</span>}
        </td>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
        }`}>
          {matches ? renderStatus(course.is_active) : <span className="text-gray-400">N/A</span>}
        </td>
        <td className={`border px-3 py-2 text-center align-middle transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
        }`}>
          <div className="flex justify-center space-x-2">
            <button 
              onClick={() => handleEdit(course)} 
              className={`p-1 rounded transition-colors duration-200 ${
                isDarkMode
                  ? "text-yellow-400 hover:text-white hover:bg-gray-700"
                  : "text-yellow-600 hover:text-yellow-800 hover:bg-gray-100"
              }`}
            >
              <FaPencilAlt className="w-4 h-4" />
            </button>
          </div>
        </td>
      </>
    );
  };

  return (
    <div className={`min-h-screen p-0 flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto w-full flex flex-col flex-grow">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className={`text-2xl sm:text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Course List
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode("grid")}
                title="Grid View"
                className={`p-2 rounded-md font-medium border transition-all duration-200 flex items-center justify-center ${
                  viewMode === "grid"
                    ? "bg-blue-900 text-white border-blue-600"
                    : isDarkMode
                      ? "text-gray-200 border-gray-500 hover:bg-blue-600 hover:text-white"
                      : "text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
                }`}
              >
                <FaTh />
              </button>
              <button
                onClick={() => setViewMode("list")}
                title="List View"
                className={`p-2 rounded-md font-medium border transition-all duration-200 flex items-center justify-center ${
                  viewMode === "list"
                    ? "bg-blue-900 text-white border-blue-600"
                    : isDarkMode
                      ? "text-gray-200 border-gray-500 hover:bg-blue-600 hover:text-white"
                      : "text-blue-900 border-blue-600 hover:bg-blue-600 hover:text-white"
                }`}
              >
                <FaBars />
              </button>
            </div>

            {/* Download Button */}
            <button 
              onClick={downloadCSV} 
              className={`px-4 py-1 rounded-md text-white font-medium transition-colors duration-200 ${
                isDarkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/30' 
                  : 'bg-blue-900 hover:bg-blue-800 shadow-lg shadow-blue-700/30'
              }`}
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Main Content Card */}
        <div className={`rounded-xl shadow-lg flex flex-col flex-grow transition-all duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-700 shadow-gray-900/50' : 'bg-white border-gray-200'
        }`}>
          {/* Header with Category Filter */}
          <div className={`px-4 py-5 sm:px-6 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'border-gray-200'
          }`}>
            <h2 className={`text-lg sm:text-xl font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>
 
            </h2>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className={`w-full px-4 py-1 border rounded-md focus:outline-none focus:ring-2 transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-gray-700'
                  }`}
                >
                  <option value="">All Categories</option>
                  {uniqueCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>

              </div>
              {(selectedCategory || Object.values(columnFilters).some(filter => filter !== "")) && (
                <button
                  onClick={clearFilters}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isDarkMode 
                      ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-600/30' 
                      : 'bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/30'
                  }`}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Courses Content */}
<div className="p-3 flex-grow">
  {viewMode === "grid" ? (
    <GridView
      data={currentCourses}
      loading={isLoading}
      renderItem={renderGridItem}
      columns={{ sm: 2, md: 3, lg: 4 }}
      skeletonCount={8}
      noDataMessage="No courses found"
      noDataSubMessage="Please select a different category or clear filters."
      addButton={{
        label: "Add Course",
        onClick: () => window.location.href = `${window.location.origin}/admin-panel/create-course`,
        icon: <FaPlus />
      }}
      isDarkMode={isDarkMode}
    />
  ) : (
    <ListView
      data={currentCourses}
      loading={isLoading}
      renderHeader={renderListHeader}
      renderRow={renderListRow}
      noDataMessage="No courses found"
      noDataSubMessage="Please select a different category or clear filters."
      addButton={{
        label: "Add Course",
        onClick: () => window.location.href = `${window.location.origin}/admin-panel/create-course`,
        icon: <FaPlus />
      }}
      isDarkMode={isDarkMode}
    />
  )}
</div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-6 pb-4 px-4">
              <span className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredCourses.length)} of {filteredCourses.length} courses
              </span>
              <div className="flex items-center space-x-2">
                {["First", "Prev", "Next", "Last"].map((label, i) => {
                  const isPrev = label === "Prev" || label === "First";
                  const isNext = label === "Next" || label === "Last";
                  const disabled = (isPrev && currentPage === 1) || (isNext && currentPage === totalPages);
                  return (
                    <button
                      key={label}
                      onClick={i === 0 ? handleFirstPage : i === 1 ? handlePrevPage : i === 2 ? handleNextPage : handleLastPage}
                      disabled={disabled}
                      className={`px-3 py-1 rounded-md border text-sm font-medium transition-all duration-200 ${
                        disabled
                          ? isDarkMode
                            ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                            : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : isDarkMode
                            ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600 shadow-lg shadow-blue-600/30"
                            : "bg-blue-900 text-white hover:bg-blue-700 border-blue-600 shadow-lg shadow-blue-600/30"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
                <span className={`px-3 py-1 font-medium text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      `}</style>
    </div>
  );
};

export default CourseList;